import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import EmptyState from "@/components/EmptyState";
import { CuteLoader } from "@/components/CuteLoader";

export default function PTOManagement() {
  const usd = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const { user } = useAuth();
  const [ptoBalance, setPtoBalance] = useState(null);
  const [ptoAsOf, setPtoAsOf] = useState(null);
  const [ptoUsed, setPtoUsed] = useState(null);
  const [requests, setRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user?._id) return;
    try {
      const [dashboardRes, requestsRes] = await Promise.all([
        api.get(`/employee/dashboard`),
        api.get(`/pto-requests?employee_id=${user._id}`)
      ]);
      setPtoBalance(dashboardRes.data.pto_balance ?? null);  // only a stored platform balance
      setPtoAsOf(dashboardRes.data.pto_as_of ?? null);
      setPtoUsed(dashboardRes.data.pto_used ?? null);
      setRequests(requestsRes.data);
    } catch (error) {
      toast.error("Failed to load PTO data");
    } finally {
      setLoading(false);
    }
  }, [user?._id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="pto-management">
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-foreground">Paid Time Off</h1>
            <p className="text-base text-muted-foreground mt-2">Manage your PTO balance and requests</p>
          </div>
        </header>

        <Card data-testid="pto-balance-card">
          <CardHeader>
            <CardTitle className="text-2xl">Your PTO Balance</CardTitle>
            <CardDescription>Available paid time off hours</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-5xl font-medium text-primary" data-testid="pto-balance-value">{ptoBalance != null ? ptoBalance.toFixed(2) : '—'}</div>
                <div className="text-sm text-muted-foreground mt-2" data-testid="pto-balance-asof">{ptoAsOf ? `Time off: ${ptoBalance} hrs available · as of ${new Date(ptoAsOf + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} (ADP)` : 'Shown once your first balance is loaded'}</div>
              </div>
              <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
                <DialogTrigger asChild>
                  <Button data-testid="request-pto-button">Request PTO</Button>
                </DialogTrigger>
                <DialogContent>
                  <PTORequestForm 
                    employeeId={user._id}
                    availableBalance={ptoBalance}
                    onSuccess={() => { 
                      setShowRequestModal(false); 
                      loadData(); 
                    }} 
                  />
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {ptoUsed && (
          <Card data-testid="pto-used-card">
            <CardHeader>
              <CardTitle className="text-xl">Time off used</CardTitle>
              <CardDescription>Vacation hours paid on each ADP check · Source: ADP</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                {ptoUsed.lines.map((l, i) => (
                  <div key={`${l.check_date}-${i}`} className="py-2 text-sm" data-testid={`pto-used-line-${i}`}>
                    {l.check_date} · {l.hours} hrs{l.amount != null ? ` — that's ${usd(l.amount)} paid for your vacation days.` : ''}
                  </div>
                ))}
                <div className="py-2 text-sm font-semibold" data-testid="pto-used-total">
                  {ptoUsed.year_total} hrs this year{ptoUsed.year_amount != null ? ` — ${usd(ptoUsed.year_amount)} paid while you were off.` : ''}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card data-testid="pto-requests-card">
          <CardHeader>
            <CardTitle className="text-xl">Your PTO Requests</CardTitle>
            <CardDescription>History and pending requests</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {requests.length === 0 ? (
                <EmptyState
                  testId="pto-empty"
                  line="No time off booked yet"
                  sub="When you request a day, it'll show up here — pending, approved, all of it."
                  compact
                />
              ) : (
                requests.map((request) => (
                  <Card key={request._id || request.id} data-testid={`pto-request-${request._id || request.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">
                            {formatDate(request.start_date)} - {formatDate(request.end_date)}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {request.hours} hours
                          </div>
                          {request.reason && (
                            <div className="text-sm text-muted-foreground mt-2">{request.reason}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-3 py-1 text-xs rounded-full ${
                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                            request.status === 'denied' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {request.status}
                          </span>
                          {request.reviewed_at && (
                            <div className="text-xs text-muted-foreground mt-2">
                              {formatDate(request.reviewed_at)}
                            </div>
                          )}
                          {request.notes && (
                            <div className="text-xs text-muted-foreground mt-2">
                              Note: {request.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PTORequestForm({ employeeId, availableBalance, onSuccess }) {
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    hours: "",
    reason: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const hours = parseFloat(formData.hours);
    if (availableBalance != null && hours > availableBalance) {
      toast.error(`Cannot request more than your available balance (${availableBalance.toFixed(1)} hours)`);
      return;
    }
    
    if (hours <= 0) {
      toast.error("Hours must be greater than zero");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/pto-requests", {
        employee_id: employeeId,
        start_date: formData.start_date,
        end_date: formData.end_date,
        hours: hours,
        reason: formData.reason
      });
      toast.success("PTO request submitted for manager approval");
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to submit PTO request";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="pto-request-form">
      <DialogHeader>
        <DialogTitle>Request Paid Time Off</DialogTitle>
        <DialogDescription>
          Available balance: {availableBalance.toFixed(1)} hours
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="start_date">Start Date</Label>
        <Input
          id="start_date"
          type="date"
          value={formData.start_date}
          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
          required
          data-testid="pto-start-date-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="end_date">End Date</Label>
        <Input
          id="end_date"
          type="date"
          value={formData.end_date}
          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
          required
          data-testid="pto-end-date-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="hours">Hours Requested</Label>
        <Input
          id="hours"
          type="number"
          step="0.5"
          value={formData.hours}
          onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
          required
          data-testid="pto-hours-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="reason">Reason (Optional)</Label>
        <Textarea
          id="reason"
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          placeholder="e.g., Family vacation, medical appointment"
          data-testid="pto-reason-input"
        />
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="submit-pto-request">
        {loading ? "Submitting..." : "Submit Request"}
      </Button>
    </form>
  );
}